/**
 * The frame of every agent location page on Fly's own hosts, at either address
 * (docs/agent-clean-addresses-plan.md, PR 1; Shardul, 22 and 23 Oct 26).
 *
 *   /app/:slug/<page>          the clean address. The slug is resolved to its
 *                              location id, then AppLocationShell opens the page
 *                              exactly as it does for an id.
 *   /app/l/:locationId/<page>  the old address. Today's page renders as it is,
 *                              and useOldAddressMove (AppLocationLegacy.tsx)
 *                              replaces the address with the clean one once the
 *                              slug is known.
 *
 * One frame for both, on purpose: React keeps a component only while the same
 * type sits in the same place, so an old address moving to its clean one (and
 * an in-app link that names the old shape while its slug is not known, PR 3
 * appPagePath) keeps the shell and the page mounted. Two frames would remount
 * the shell on every tap, with its "Opening" screen and its session check each
 * time.
 *
 * A clean address, in order:
 *   1. a reserved word or a malformed segment: NotFound, as today
 *   2. upper case: moved to lower case, query and hash kept
 *   3. a slug this tab already knows for the person signed in (an overview
 *      or app_address told it): the page opens at once; AppLocationShell
 *      still checks the session and the location. Slugs are per person since
 *      PR 1b (the same address names each owner's own copy), so the map is
 *      trusted only while it holds this person's slugs (appSlugsAreFor)
 *   4. else the session (signed out: sign in, then back here), the overview
 *      (it lists every location of the caller's workspaces with its slug),
 *      then agent-action-respond app_address
 *   5. an old slug (an alias) moves to the current one, page, query and hash
 *      kept; not found or refused shows the page a foreign /app/l/<uuid>
 *      shows today; offline or a server error says so, with Try again
 *
 * On either shape, a page's old word moves to its words now (PR 6; Shardul,
 * 23 Oct 26): /app/<slug>/ideas?tab=calendar#x becomes
 * /app/<slug>/posts?tab=calendar#x, /app/l/<uuid>/keywords becomes
 * /app/l/<uuid>/growth/local-rank (which then moves to the slug as above). A
 * replace, with nothing asked of the server. The page an old word opens is
 * the page its words open, so where the page can already be shown it is, and
 * it stays mounted through the move; anything that would decide on the old
 * address (sign in, an alias, the gone page) waits for the words. On an
 * agency's host nothing moves (appAddressWithPageWords answers null there).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { signInPath } from '@/lib/agentCaller';
import { appAddressWithPageWords, appSlugsAreFor, isAppSlug, locationIdForSlug, rememberAppSlugs, withSlug } from '@/lib/appAddress';
import { FunnelLoader } from '@/components/agent-shell/FunnelBoundary';
import NotFound from '@/pages/NotFound';
import { fetchOverview, knownSessionUser } from '../appData';
import { AppLocationShell, GonePage } from './AppLocationShell';
import { askAppAddress, useOldAddressMove } from './AppLocationLegacy';
import { rememberLocationOpens } from './locationAccess';

const LOG = '[AppSlugShell]';

export const SLUG_ERROR_TITLE = 'This page could not be opened.';
export const SLUG_ERROR_LINE = 'Check your connection and try again.';
export const SLUG_ERROR_ACTION = 'Try again';

export type SlugAnswer =
  | { kind: 'found'; locationId: string }
  /** An old slug: the current one to move to. */
  | { kind: 'moved'; slug: string; locationId: string }
  | { kind: 'signed_out' }
  | { kind: 'gone' }
  | { kind: 'error' };

/** Step 4 above, for a slug this tab does not know yet. */
export async function resolveAppSlug(slug: string): Promise<SlugAnswer> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id ?? null;
  if (!userId) return { kind: 'signed_out' };

  const overview = await fetchOverview(false);
  if (overview.kind === 'ok' && overview.userId === userId) {
    for (const ws of overview.value?.workspaces ?? []) rememberAppSlugs(ws?.locations, userId);
    const listed = locationIdForSlug(slug);
    if (listed) return { kind: 'found', locationId: listed };
  }

  const asked = await askAppAddress({ slug }, userId);
  if (asked.kind === 'refused') {
    console.log(`${LOG} the slug does not open on this account`, { status: asked.status });
    return { kind: 'gone' };
  }
  if (asked.kind === 'error') {
    console.warn(`${LOG} could not resolve the slug`, { status: asked.status });
    return { kind: 'error' };
  }
  // The server opened it for this session with the rule every /app op uses, so the shell needs no second check.
  rememberLocationOpens(asked.answer.location_id, userId);
  // askAppAddress put this person's current slug for the location in the map.
  if (asked.answer.slug !== slug) return { kind: 'moved', slug: asked.answer.slug, locationId: asked.answer.location_id };
  return { kind: 'found', locationId: asked.answer.location_id };
}

function SlugErrorPage({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="ag-standalone flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="ag-standalone-card p-6 text-center" role="status">
          <h1 className="mb-2 text-xl font-semibold ag-standalone-ink">{SLUG_ERROR_TITLE}</h1>
          <p className="mb-6">{SLUG_ERROR_LINE}</p>
          <button type="button" className="ag-standalone-btn ag-focus w-full" onClick={onRetry}>
            {SLUG_ERROR_ACTION}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The same address with its first segment written another way (lower case), query and hash kept. */
function withFirstSegment(pathname: string, segment: string): string {
  return pathname.replace(/^\/app\/[^/]+/, `/app/${segment}`);
}

export function AppSlugShell({ children }: { children: ReactNode }) {
  const params = useParams<{ slug?: string; locationId?: string }>();
  const location = useLocation();
  const slugParam = params.slug;
  // An old page word moves first; the old address moves to its slug once the words are the page's own.
  const wordMove = appAddressWithPageWords(location.pathname);
  useOldAddressMove(slugParam === undefined && !wordMove ? params.locationId : undefined);

  const valid = slugParam !== undefined && isAppSlug(slugParam);
  // Only this person's own slugs: the same address may name another copy for someone else.
  const known = valid && appSlugsAreFor(knownSessionUser()) ? locationIdForSlug(slugParam) : null;
  const [answer, setAnswer] = useState<{ slug: string; value: SlugAnswer } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const settled = valid && !known && answer?.slug === slugParam ? answer.value : null;

  useEffect(() => {
    if (!valid || known || !slugParam) return undefined;
    let alive = true;
    void resolveAppSlug(slugParam)
      .catch((err): SlugAnswer => {
        console.warn(`${LOG} slug lookup failed`, err);
        return { kind: 'error' };
      })
      .then((value) => {
        if (!alive) return;
        setAnswer({ slug: slugParam, value });
      });
    return () => {
      alive = false;
    };
  }, [valid, known, slugParam, attempt]);

  const retry = useCallback(() => {
    setAnswer(null);
    setAttempt((n) => n + 1);
  }, []);

  // Every answer carries the word move beside it, so the frame keeps one shape and the shell
  // and the page stay mounted through the move (both words render the same page).
  const moveWords = wordMove ? <Navigate to={{ pathname: wordMove, search: location.search, hash: location.hash }} replace /> : null;
  const out = (body: ReactNode) => (
    <>
      {body}
      {moveWords}
    </>
  );

  // The old address: today's page, as it is; the hook above moves it.
  if (slugParam === undefined) {
    return out(<AppLocationShell locationId={params.locationId}>{children}</AppLocationShell>);
  }

  const lower = slugParam.toLowerCase();
  if (!valid) {
    if (lower !== slugParam && isAppSlug(lower)) {
      return out(<Navigate to={{ pathname: withFirstSegment(location.pathname, lower), search: location.search, hash: location.hash }} replace />);
    }
    return out(<NotFound />);
  }

  const locationId = known ?? (settled?.kind === 'found' ? settled.locationId : null);
  if (locationId) return out(<AppLocationShell locationId={locationId}>{children}</AppLocationShell>);

  // While an old word moves nothing else is decided here: the address it moves to decides.
  if (!settled || wordMove) return out(<FunnelLoader label="Opening" />);
  if (settled.kind === 'signed_out') {
    return out(<Navigate to={signInPath(`${location.pathname}${location.search}${location.hash}`)} replace />);
  }
  if (settled.kind === 'moved') {
    const to = withSlug(location.pathname, settled.slug);
    if (to) return out(<Navigate to={{ pathname: to, search: location.search, hash: location.hash }} replace />);
    return out(<GonePage />);
  }
  if (settled.kind === 'error') return out(<SlugErrorPage onRetry={retry} />);
  return out(<GonePage />);
}

export default AppSlugShell;
